import { Injectable } from '@angular/core';
import { NavigationEnd, Router, ActivatedRouteSnapshot } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';

interface Breadcrumb {
  label: string;
  url: string;
  data: any
}

@Injectable({
  providedIn: 'root',
})
export class BreadCrumbsService {
  private breadcrumbs = new BehaviorSubject<Breadcrumb[]>([]);
  breadcrumbs$ = this.breadcrumbs.asObservable();
  routeData: any;
  constructor(private router: Router) {
    this.routeData = history.state.data;
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.routeData = history.state.data;
      });
    // Initialize breadcrumbs immediately for the current route on reload
    this.setupInitialBreadcrumbs();

    // Then subscribe to route changes and update breadcrumbs accordingly
    this.initializeBreadcrumbsOnNavigation();
  }

  // Function to handle initialization of breadcrumbs on page reload
  private setupInitialBreadcrumbs() {
    const root = this.router.routerState.snapshot.root;
    const breadcrumbs: Breadcrumb[] = [];
    this.addBreadcrumbs(root, '', breadcrumbs);
    this.breadcrumbs.next(breadcrumbs);
  }

  // Subscribe to NavigationEnd events to update breadcrumbs on navigation
  private initializeBreadcrumbsOnNavigation() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        const root = this.router.routerState.snapshot.root;
        const breadcrumbs: Breadcrumb[] = [];
        this.addBreadcrumbs(root, '', breadcrumbs);
        this.breadcrumbs.next(breadcrumbs);
      });
  }

  // Recursive method to generate breadcrumbs
  private addBreadcrumbs(route: ActivatedRouteSnapshot, parentUrl: string, breadcrumbs: Breadcrumb[]) {
    if (!route) return;

    const routeUrl = route.url.map((urlSegment) => urlSegment.path).join('/');
    const fullUrl = parentUrl + (routeUrl ? `/${routeUrl}` : '');
    if (route.data && route.data['title']) {
      let label = route.data['title'];
      // If this is the "details" route, use the name parameter in the URL
      if (route.data['title'] === 'Patient Profile' && this.routeData) {
        label = this.routeData.name ? `${this.routeData.name}` : `${this.routeData.firstName} ${this.routeData.lastName}`
      }

      if ((route.data['title'] === 'Emergency Contacts' || route.data['title'] === 'Related Profiles') && this.routeData) {
        breadcrumbs.push(
          {
            label: this.routeData.name ? `${this.routeData.name}` : `${this.routeData.firstName} ${this.routeData.lastName}`,
            url: `/patients/${this.routeData?.id}`,
            data: this.routeData
          }
        )
      }

      if (!breadcrumbs.some((b) => b.url === fullUrl)) {
        breadcrumbs.push({ label, url: fullUrl, data: this.routeData });
      }
    }

    if (route.firstChild) {
      this.addBreadcrumbs(route.firstChild, fullUrl, breadcrumbs);
    }
  }
}
